import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_PRIORITIES } from "../../../utils/constants";
import { PriorityInterface } from "../../../models/TicketModule/Priority";
import priorityService from "../../../services/ticketService/priorityService";

interface UpdatePrioritiesContext {
  previousPriorities: PriorityInterface[];
}

const useUpdatePriority = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    PriorityInterface,
    Error,
    PriorityInterface,
    UpdatePrioritiesContext
  >({
    mutationFn: async (priority: PriorityInterface) => {
      // Assuming the ID is stored in the priority object or can be derived
      const { id, ...priorityData } = priority;
      // return priorityService.Update(id || "", priorityData);
      return priorityService.Update(id || "", priorityData);
    },

    onMutate: async (updatedPriority: PriorityInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousPriorities =
        queryClient.getQueryData<PriorityInterface[]>([
          CACHE_KEY_PRIORITIES,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<PriorityInterface[]>(
        [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (priorities = []) =>
          priorities.map((priority) =>
            priority.id === updatedPriority.id ? updatedPriority : priority
          )
      );

      return { previousPriorities };
    },

    onSuccess: (savedPriority) => {
      queryClient.setQueryData<PriorityInterface[]>(
        [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (priorities) =>
          priorities?.map((priority) =>
            priority.id === savedPriority.id ? savedPriority : priority
          )
      );
    },

    onError: (error, updatedPriority, context) => {
      if (!context) return;

      queryClient.setQueryData<PriorityInterface[]>(
        [
          CACHE_KEY_PRIORITIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousPriorities
      );
    },
  });
};

export default useUpdatePriority;
